import { XMarkIcon } from '@heroicons/react/24/outline';
import {
  CheckCircleIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
  XCircleIcon,
} from '@heroicons/react/24/solid';
import { cn } from '@/utils/cn';

interface AlertProps {
  variant?: 'info' | 'success' | 'warning' | 'error';
  title?: string;
  children: React.ReactNode;
  onClose?: () => void;
  className?: string;
}

const alertConfig = {
  info: {
    containerClass: 'bg-info/10 border-info/30',
    iconClass: 'text-info',
    titleClass: 'text-info',
    textClass: 'text-info',
    Icon: InformationCircleIcon,
  },
  success: {
    containerClass: 'bg-success/10 border-success/30',
    iconClass: 'text-success',
    titleClass: 'text-success',
    textClass: 'text-success',
    Icon: CheckCircleIcon,
  },
  warning: {
    containerClass: 'bg-warning/10 border-warning/30',
    iconClass: 'text-warning',
    titleClass: 'text-warning',
    textClass: 'text-warning',
    Icon: ExclamationTriangleIcon,
  },
  error: {
    containerClass: 'bg-danger/10 border-danger/30',
    iconClass: 'text-danger',
    titleClass: 'text-danger',
    textClass: 'text-danger',
    Icon: XCircleIcon,
  },
};

function Alert({ variant = 'info', title, children, onClose, className }: AlertProps) {
  const config = alertConfig[variant];
  const Icon = config.Icon;

  return (
    <div
      className={cn(
        'rounded-lg border p-4',
        config.containerClass,
        className
      )}
      role="alert"
    >
      <div className="flex">
        <div className="flex-shrink-0">
          <Icon className={cn('h-5 w-5', config.iconClass)} aria-hidden="true" />
        </div>
        <div className="ml-3 flex-1">
          {title && (
            <h3 className={cn('text-sm font-medium', config.titleClass)}>
              {title}
            </h3>
          )}
          <div className={cn('text-sm', config.textClass, title && 'mt-2')}>
            {children}
          </div>
        </div>
        {onClose && (
          <div className="ml-auto pl-3">
            <button
              type="button"
              onClick={onClose}
              className={cn(
                'inline-flex rounded-md p-1.5 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-dark-base',
                config.iconClass,
                'hover:bg-white/10'
              )}
              aria-label="Dismiss"
            >
              <XMarkIcon className="h-5 w-5" aria-hidden="true" />
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default Alert;
